import { Skeleton } from "@design-system/DataDisplay/Skeleton";
import { Button } from "@design-system/Inputs/Button";
import { FlexRow } from "@design-system/Layout/Flex";
import { cn } from "@design-system/Utilities";
import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    type UniqueIdentifier,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type FC, type PropsWithChildren } from "react";

type SortableListProps<T extends { id: string }> = PropsWithChildren<{
    items: T[];
    onItemsChange: (
        activeItemId: UniqueIdentifier,
        overItemId: UniqueIdentifier | undefined,
    ) => void;
}>;

const SortableItem: FC<
    PropsWithChildren<{ id: string; className?: string }>
> = ({ children, id, className }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <FlexRow
            gap="2"
            p="2"
            w="full"
            alignItems="center"
            className={cn(
                "border border-tertiary",
                isDragging && "z-10 cursor-grabbing",
                className,
            )}
            justifyContent="between"
            br="lg"
            ref={setNodeRef}
            style={style}
        >
            {children}
            <Button
                variant="text"
                size="sm"
                className={isDragging ? "cursor-grabbing" : "cursor-grab"}
                {...attributes}
                {...listeners}
            >
                <Button.Icon name="gripVertical" />
            </Button>
        </FlexRow>
    );
};

const SortableItemSkeleton = () => {
    return (
        <FlexRow
            gap="2"
            p="2"
            w="full"
            alignItems="center"
            className="border border-tertiary"
            justifyContent="between"
            br="lg"
        >
            <Skeleton className="h-4 w-full" />
            <Button variant="text" size="sm">
                <Button.Icon name="gripVertical" />
            </Button>
        </FlexRow>
    );
};

export const SortableList = <T extends { id: string }>({
    items,
    children,
    onItemsChange,
}: SortableListProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        onItemsChange(active.id, over?.id);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={items}>{children}</SortableContext>
        </DndContext>
    );
};

SortableList.SortableItem = SortableItem;
SortableList.SortableItemSkeleton = SortableItemSkeleton;
